import { useMemo } from "react";
import { TicketInterface } from "../../../../models/TicketModule/Ticket";
import { StatusInterface } from "../../../../models/TicketModule/Status";

type FilterOptions = {
  searchText: string;
  selectedMembers: string[]; // from your state
  selectedStatuses: string[]; // from your state
};

export function useTicketFiltering(tickets: TicketInterface[], statuses: StatusInterface[], filters: FilterOptions) {
  return useMemo(() => {
    const query = filters.searchText.trim().toLowerCase();
    const hasSearch = query.length >= 2;

    const memberSet = filters.selectedMembers?.length ? new Set(filters.selectedMembers) : null;

    const statusSet = filters.selectedStatuses?.length ? new Set(filters.selectedStatuses) : null;

    // 1) filter tickets once
    const filtered = (tickets || []).filter((t) => {
      if (memberSet && !memberSet.has(t.assignedToId || "")) return false;
      if (statusSet && !statusSet.has(t.statusId || "")) return false;

      if (!hasSearch) return true;

      const assigneeName = t.assignedTo ? `${t.assignedTo.firstname} ${t.assignedTo.lastname}`.toLowerCase() : "";

      const title = (t.title || "").toLowerCase();
      const category = (t.category?.title || "").toLowerCase();

      return title.includes(query) || assigneeName.includes(query) || category.includes(query);
    });

    // 2) group by status once (for columns)
    const byStatus = new Map<string, TicketInterface[]>();
    for (const t of filtered) {
      const key = t.statusId || "";
      if (!byStatus.has(key)) byStatus.set(key, []);
      byStatus.get(key)!.push(t);
    }

    // 3) visible statuses sorted
    const visibleStatuses = (statuses || [])
      .filter((s) => !statusSet || statusSet.has(s.id || ""))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return {
      filteredTickets: filtered,
      byStatus,
      visibleStatuses,
      totalCount: filtered.length,
    };
  }, [tickets, statuses, filters.searchText, filters.selectedMembers, filters.selectedStatuses]);
}
